import type { DraggableChildrenFn } from '@hello-pangea/dnd';
import { Droppable as DroppableDnd } from '@hello-pangea/dnd';
import clsx from 'clsx';

import { Stack } from '../layout';
import classes from './droppable.module.css';

interface DroppableProps {
  children: React.ReactNode;
  droppableId: string;
  type?: string;
  isDragging?: boolean;
  isDropDisabled?: boolean;
  /**
   * Renders a clone of the dragged item to document.body during drag.
   * Use this to fix positioning issues when the Droppable is inside
   * scroll containers, iframes, or elements with CSS transforms.
   */
  renderClone?: DraggableChildrenFn;
}

/**
 * ### Droppable
 * Standardized droppable component from @hello-pangea/dnd
 * with custom styling for dragging and dropping.
 *
 * @example
 * ```tsx
 * <Droppable droppableId="droppable-id">
 *   <div>Droppable content</div>
 * </Droppable>
 * ```
 * @param children - The children to render inside the droppable container
 * @param droppableId - The id of the droppable container
 * @param type - The type of the droppable container
 * @param isDragging - Whether the droppable container is dragging
 * @param renderClone - Optional render function for the dragged clone (fixes positioning in scroll containers)
 * @returns The droppable container
 */
const Droppable = ({
  children,
  droppableId,
  type,
  isDragging = false,
  isDropDisabled = false,
  renderClone,
}: DroppableProps) => {
  return (
    <DroppableDnd
      droppableId={droppableId}
      type={type}
      isDropDisabled={isDropDisabled}
      renderClone={renderClone}
    >
      {(provided) => (
        <Stack
          ref={provided.innerRef}
          {...provided.droppableProps}
          gap="sm"
          className={clsx(classes.root, isDragging && classes.dragging)}
        >
          {children}
          {provided.placeholder}
        </Stack>
      )}
    </DroppableDnd>
  );
};

export default Droppable;
